import {
  uploadDiagnosticDurationMs,
  uploadDiagnosticLog,
  uploadDiagnosticLogThrottled,
  uploadDiagnosticStart,
} from "./uploadDiagnostics";

export const SOURCE_HANDLE_PERMISSION = {
  AVAILABLE: "AVAILABLE",
  NEEDS_RESELECT: "NEEDS_RESELECT",
  NOT_REQUIRED: "NOT_REQUIRED",
  PERMISSION_DENIED: "PERMISSION_DENIED",
  UNSUPPORTED: "UNSUPPORTED",
  UNKNOWN: "UNKNOWN",
};

function normalizePathPart(pathPart) {
  return String(pathPart || "").replace(/\\/g, "/").replace(/^\/+|\/+$/g, "");
}

function joinPath(parts) {
  return parts.map(normalizePathPart).filter(Boolean).join("/");
}

function randomHandleId(prefix) {
  if (window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function yieldToBrowser() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function fileMetadata(file) {
  return {
    name: file.name,
    size: file.size,
    type: file.type || "",
    lastModified: file.lastModified || null,
  };
}

export function isFileSystemAccessSupported(win = window) {
  const supported = !!(
    win?.isSecureContext &&
    typeof win.showOpenFilePicker === "function" &&
    typeof win.showDirectoryPicker === "function" &&
    typeof win.FileSystemFileHandle !== "undefined" &&
    typeof win.FileSystemDirectoryHandle !== "undefined"
  );
  uploadDiagnosticLogThrottled("debug", "file-system-access-support-detected", supported ? "supported" : "unsupported", { supported });
  return supported;
}

export function isPickerCancel(error) {
  return error?.name === "AbortError";
}

export function isFallbackPickerError(error) {
  return ["SecurityError", "NotSupportedError", "TypeError"].includes(error?.name);
}

export async function fileHandleToUploadItem(handle, pathPrefix = "", sourceRootHandle = null, sourceRootHandleId = null) {
  const file = await handle.getFile();
  const sourceHandleId = randomHandleId("file-handle");
  return {
    file: fileMetadata(file),
    path: joinPath([pathPrefix, file.name]),
    sourceHandle: handle,
    sourceHandleId,
    sourceHandleKind: "FILE",
    sourceHandlePermissionState: SOURCE_HANDLE_PERMISSION.AVAILABLE,
    sourceRootHandle,
    sourceRootHandleId,
    sourceRootHandleKind: sourceRootHandle ? "DIRECTORY" : null,
  };
}

export async function enumerateDirectoryHandle(directoryHandle, pathPrefix = "", sourceRootHandle = directoryHandle, sourceRootHandleId = null) {
  const startedAt = uploadDiagnosticStart();
  const rootHandleId = sourceRootHandleId || randomHandleId("directory-handle");
  const entries = [];
  for await (const [name, handle] of directoryHandle.entries()) {
    entries.push({ name, handle });
  }
  entries.sort((a, b) => {
    if (a.handle.kind !== b.handle.kind) {
      return a.handle.kind === "directory" ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  const files = [];
  for (const entry of entries) {
    const relativePrefix = joinPath([pathPrefix, entry.name]);
    if (entry.handle.kind === "directory") {
      files.push(...await enumerateDirectoryHandle(entry.handle, relativePrefix, sourceRootHandle, rootHandleId));
    } else if (entry.handle.kind === "file") {
      files.push(await fileHandleToUploadItem(entry.handle, pathPrefix, sourceRootHandle, rootHandleId));
    }
  }
  uploadDiagnosticLog("debug", "file-system-access-directory-enumerated", {
    sourceRootHandleId: rootHandleId,
    entryCount: entries.length,
    fileCount: files.length,
    durationMs: uploadDiagnosticDurationMs(startedAt),
  });
  return files;
}

export async function pickFilesWithFileSystemAccess(win = window, options = {}) {
  const startedAt = uploadDiagnosticStart();
  uploadDiagnosticLog("info", "picker-opened", { pickerPath: "file-system-access-files" });
  const handles = await win.showOpenFilePicker({ multiple: true });
  options.onSelectionAccepted?.({ pickerPath: "file-system-access-files", handleCount: handles.length });
  await yieldToBrowser();
  const normalizationStartedAt = uploadDiagnosticStart();
  const items = [];
  for (const handle of handles) {
    items.push(await fileHandleToUploadItem(handle));
    await yieldToBrowser();
  }
  const sortedItems = items.sort((a, b) => a.path.localeCompare(b.path));
  uploadDiagnosticLog("info", "picker-selection-normalized", {
    pickerPath: "file-system-access-files",
    handleCount: handles.length,
    fileCount: sortedItems.length,
    selectionDurationMs: uploadDiagnosticDurationMs(startedAt),
    normalizationDurationMs: uploadDiagnosticDurationMs(normalizationStartedAt),
  });
  return sortedItems;
}

export async function pickDirectoryWithFileSystemAccess(win = window, options = {}) {
  const startedAt = uploadDiagnosticStart();
  uploadDiagnosticLog("info", "picker-opened", { pickerPath: "file-system-access-directory" });
  const directoryHandle = await win.showDirectoryPicker();
  options.onSelectionAccepted?.({ pickerPath: "file-system-access-directory", directoryName: directoryHandle.name });
  await yieldToBrowser();
  const rootHandleId = randomHandleId("directory-handle");
  const normalizationStartedAt = uploadDiagnosticStart();
  const items = await enumerateDirectoryHandle(directoryHandle, directoryHandle.name, directoryHandle, rootHandleId);
  const sortedItems = items.sort((a, b) => a.path.localeCompare(b.path));
  uploadDiagnosticLog("info", "picker-selection-normalized", {
    pickerPath: "file-system-access-directory",
    sourceRootHandleId: rootHandleId,
    fileCount: sortedItems.length,
    selectionDurationMs: uploadDiagnosticDurationMs(startedAt),
    normalizationDurationMs: uploadDiagnosticDurationMs(normalizationStartedAt),
  });
  return sortedItems;
}

async function resolveFileFromDirectory(directoryHandle, pathParts) {
  if (!pathParts.length) {
    return null;
  }
  let current = directoryHandle;
  for (const part of pathParts.slice(0, -1)) {
    current = await current.getDirectoryHandle(part);
  }
  return await current.getFileHandle(pathParts[pathParts.length - 1]);
}

function retryDirectoryPathCandidates(relativePath, selectedDirectoryName) {
  const parts = joinPath([relativePath]).split("/").filter(Boolean);
  if (!parts.length) {
    return [];
  }
  const candidates = [parts];
  if (selectedDirectoryName && parts[0] === selectedDirectoryName && parts.length > 1) {
    candidates.push(parts.slice(1));
  }
  return candidates;
}

export async function pickDirectoryRetryItemsWithFileSystemAccess(win = window, entries = [], options = {}) {
  const startedAt = uploadDiagnosticStart();
  uploadDiagnosticLog("info", "picker-opened", { pickerPath: "file-system-access-directory-retry" });
  const directoryHandle = await win.showDirectoryPicker();
  options.onSelectionAccepted?.({ pickerPath: "file-system-access-directory-retry", directoryName: directoryHandle.name });
  await yieldToBrowser();
  const rootHandleId = randomHandleId("directory-handle");
  const normalizationStartedAt = uploadDiagnosticStart();
  const items = [];
  const fileEntries = (entries || []).filter((entry) => String(entry?.entryType || "FILE") === "FILE");

  for (const entry of fileEntries) {
    const relativePath = joinPath([entry.relativePath || entry.path || entry.name]);
    let handle = null;
    for (const candidate of retryDirectoryPathCandidates(relativePath, directoryHandle.name)) {
      try {
        handle = await resolveFileFromDirectory(directoryHandle, candidate);
        break;
      } catch (error) {
        if (error?.name !== "NotFoundError") {
          throw error;
        }
      }
    }
    if (handle) {
      items.push(await fileHandleToUploadItem(handle, relativePath.split("/").slice(0, -1).join("/"), directoryHandle, rootHandleId));
    }
    await yieldToBrowser();
  }

  uploadDiagnosticLog("info", "picker-selection-normalized", {
    pickerPath: "file-system-access-directory-retry",
    sourceRootHandleId: rootHandleId,
    requestedFileCount: fileEntries.length,
    fileCount: items.length,
    selectionDurationMs: uploadDiagnosticDurationMs(startedAt),
    normalizationDurationMs: uploadDiagnosticDurationMs(normalizationStartedAt),
  });
  return items.sort((a, b) => a.path.localeCompare(b.path));
}

export async function requestHandleReadPermission(handle) {
  if (!handle) {
    return SOURCE_HANDLE_PERMISSION.NEEDS_RESELECT;
  }
  try {
    const query = typeof handle.queryPermission === "function"
      ? await handle.queryPermission({ mode: "read" })
      : "granted";
    if (query === "granted") {
      return SOURCE_HANDLE_PERMISSION.AVAILABLE;
    }
    const requested = typeof handle.requestPermission === "function"
      ? await handle.requestPermission({ mode: "read" })
      : query;
    return requested === "granted"
      ? SOURCE_HANDLE_PERMISSION.AVAILABLE
      : SOURCE_HANDLE_PERMISSION.PERMISSION_DENIED;
  } catch (error) {
    uploadDiagnosticLog("warn", "file-system-access-permission-check-failed", {}, error);
    return SOURCE_HANDLE_PERMISSION.PERMISSION_DENIED;
  }
}

export async function pickLegacyRetryItems({ directory = false } = {}) {
  return await new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    if (directory) {
      input.webkitdirectory = true;
    }
    input.style.display = "none";
    input.onchange = () => {
      const items = Array.from(input.files || []).map((file) => ({
        file,
        path: file.webkitRelativePath || file.name,
      }));
      document.body.removeChild(input);
      resolve(items);
    };
    input.onerror = () => {
      document.body.removeChild(input);
      reject(new Error("Retry file selection failed"));
    };
    document.body.appendChild(input);
    input.click();
  });
}
